import { debounce } from '@/lib/utils'
import { createContext, useContext, useEffect, useRef, useState } from 'react'

type TDeepBrowsingContext = {
  deepBrowsing: boolean
  lastScrollTop: number
}

const DeepBrowsingContext = createContext<TDeepBrowsingContext | undefined>(undefined)

export const useDeepBrowsing = () => {
  const context = useContext(DeepBrowsingContext)
  if (!context) {
    throw new Error('useDeepBrowsing must be used within a DeepBrowsingProvider')
  }
  return context
}

export function DeepBrowsingProvider({
  children,
  active,
  scrollAreaRef
}: {
  children: React.ReactNode
  active: boolean
  scrollAreaRef?: React.RefObject<HTMLDivElement>
}) {
  const [deepBrowsing, setDeepBrowsing] = useState(false)
  const lastScrollTopRef = useRef(
    (!scrollAreaRef ? window.scrollY : scrollAreaRef.current?.scrollTop) || 0
  )
  const [lastScrollTop, setLastScrollTop] = useState(lastScrollTopRef.current)

  useEffect(() => {
    if (!active) return

    let ticking = false

    const handleScroll = debounce(() => {
      if (ticking) return
      ticking = true

      requestAnimationFrame(() => {
        ticking = false
        const scrollTop = (!scrollAreaRef ? window.scrollY : scrollAreaRef.current?.scrollTop) || 0
        const diff = scrollTop - lastScrollTopRef.current

        // Bail out if nothing meaningfully changed, avoids redundant re-renders
        if (scrollTop === lastScrollTopRef.current) return

        lastScrollTopRef.current = scrollTop
        setLastScrollTop(scrollTop)

        if (scrollTop <= 800) {
          setDeepBrowsing(false)
          return
        }

        if (diff > 20) {
          setDeepBrowsing(true)
        } else if (diff < -20) {
          setDeepBrowsing(false)
        }
      })
    }, 400)

    const target = scrollAreaRef ? scrollAreaRef.current : window

    target?.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      target?.removeEventListener('scroll', handleScroll)
    }
  }, [active])

  return (
    <DeepBrowsingContext.Provider value={{ deepBrowsing, lastScrollTop }}>
      {children}
    </DeepBrowsingContext.Provider>
  )
}
